<template>
  <div class="parametro-valor-input">

    <!-- Toggle fixo / relativo (apenas para date com permiteRelativo) -->
    <div v-if="mostraToggleRelativo" class="pvi-toggle">
      <button
        type="button"
        class="pvi-toggle__btn"
        :class="{ active: tipoValor === 'fixo' }"
        @click="setTipoValor('fixo')"
      >Fixo</button>
      <button
        type="button"
        class="pvi-toggle__btn"
        :class="{ active: tipoValor === 'relativo' }"
        @click="setTipoValor('relativo')"
      >Relativo</button>
    </div>

    <!-- Valor relativo -->
    <ValorRelativoSelect
      v-if="tipoValor === 'relativo'"
      :value="valorInterno"
      @input="onInputRelativo"
    />

    <!-- Valor fixo -->
    <template v-else>

      <!-- Entidade autocomplete -->
      <EntidadeAutocomplete
        v-if="entidadeRef"
        :value="valorInterno"
        :entidade="entidadeRef"
        :multiple="tipo === 'multiselect'"
        :placeholder="placeholder"
        @input="onInputFixo"
      />

      <!-- Select simples -->
      <select
        v-else-if="tipo === 'select'"
        class="form-control form-control-sm"
        :value="valorInterno"
        @change="onInputFixo($event.target.value)"
      >
        <option value="">Selecione</option>
        <option
          v-for="opt in normalizedOpcoes"
          :key="opt.value"
          :value="opt.value"
        >{{ opt.label }}</option>
      </select>

      <!-- Multiselect (com opcoes estaticas) -->
      <div v-else-if="tipo === 'multiselect'" class="multi-select-container">
        <div class="multi-select-tags">
          <span
            v-for="val in (Array.isArray(valorInterno) ? valorInterno : [])"
            :key="val"
            class="multi-select-tag"
          >
            {{ getOptionLabel(val) }}
            <span class="multi-select-tag-remove" @click.stop="toggleMulti(val)">&times;</span>
          </span>
          <span v-if="!Array.isArray(valorInterno) || !valorInterno.length" class="multi-select-placeholder">
            {{ placeholder || "Selecione" }}
          </span>
        </div>
        <div class="multi-select-options">
          <label
            v-for="opt in normalizedOpcoes"
            :key="opt.value"
            class="multi-select-option"
            @click.stop
          >
            <input
              type="checkbox"
              :checked="(Array.isArray(valorInterno) ? valorInterno : []).includes(opt.value)"
              @change="toggleMulti(opt.value)"
            />
            {{ opt.label }}
          </label>
        </div>
      </div>

      <!-- Boolean -->
      <select
        v-else-if="tipo === 'boolean'"
        class="form-control form-control-sm"
        :value="valorInterno"
        @change="onInputFixo($event.target.value)"
      >
        <option value="">Selecione</option>
        <option value="true">Sim</option>
        <option value="false">Nao</option>
      </select>

      <!-- Number -->
      <input
        v-else-if="tipo === 'number'"
        type="number"
        class="form-control form-control-sm"
        :value="valorInterno"
        :placeholder="placeholder || 'Informe o valor'"
        @input="onInputFixo($event.target.value)"
      />

      <!-- Date -->
      <input
        v-else-if="tipo === 'date'"
        type="date"
        class="form-control form-control-sm"
        :value="valorInterno"
        @input="onInputFixo($event.target.value)"
      />

      <!-- Text (default) -->
      <input
        v-else
        type="text"
        class="form-control form-control-sm"
        :value="valorInterno"
        :placeholder="placeholder || 'Informe o valor'"
        @input="onInputFixo($event.target.value)"
      />
    </template>
  </div>
</template>

<script>
import ValorRelativoSelect from "./ValorRelativoSelect.vue";
import EntidadeAutocomplete from "./EntidadeAutocomplete.vue";

function unwrap(value) {
  if (value && typeof value === "object" && !Array.isArray(value) && "tipo_valor" in value) {
    return { tipoValor: value.tipo_valor || "fixo", valor: value.valor };
  }
  return { tipoValor: "fixo", valor: value };
}

export default {
  name: "ParametroValorInput",
  components: { ValorRelativoSelect, EntidadeAutocomplete },
  props: {
    value: {
      default: null,
    },
    tipo: {
      type: String,
      default: "text",
      validator: (v) => ["text", "number", "date", "boolean", "select", "multiselect"].includes(v),
    },
    opcoes: {
      type: Array,
      default: () => [],
    },
    permiteRelativo: {
      type: Boolean,
      default: false,
    },
    entidadeRef: {
      type: String,
      default: "",
    },
    placeholder: {
      type: String,
      default: "",
    },
  },
  data() {
    const init = unwrap(this.value);
    return {
      tipoValor: init.tipoValor,
      valorInterno: init.valor != null ? init.valor : (this.tipo === "multiselect" ? [] : ""),
    };
  },
  computed: {
    mostraToggleRelativo() {
      return this.permiteRelativo && this.tipo === "date";
    },
    normalizedOpcoes() {
      return (this.opcoes || []).map((opt) => {
        if (opt && typeof opt === "object") {
          return {
            value: opt.value != null ? opt.value : opt.id,
            label: opt.label != null ? opt.label : (opt.descricao || String(opt.value || opt.id)),
          };
        }
        return { value: opt, label: String(opt) };
      });
    },
  },
  watch: {
    value(novo) {
      const u = unwrap(novo);
      this.tipoValor = u.tipoValor;
      this.valorInterno = u.valor != null ? u.valor : (this.tipo === "multiselect" ? [] : "");
    },
  },
  methods: {
    emitChange() {
      this.$emit("input", {
        tipo_valor: this.tipoValor,
        valor: this.valorInterno,
      });
    },
    setTipoValor(t) {
      if (this.tipoValor === t) return;
      this.tipoValor = t;
      // resetar valor ao trocar modo
      this.valorInterno = this.tipo === "multiselect" ? [] : "";
      this.emitChange();
    },
    onInputFixo(val) {
      this.valorInterno = val;
      this.emitChange();
    },
    onInputRelativo(slug) {
      this.valorInterno = slug;
      this.emitChange();
    },
    getOptionLabel(val) {
      const opt = this.normalizedOpcoes.find((o) => o.value === val);
      return opt ? opt.label : val;
    },
    toggleMulti(val) {
      const current = Array.isArray(this.valorInterno) ? [...this.valorInterno] : [];
      const idx = current.indexOf(val);
      if (idx >= 0) current.splice(idx, 1);
      else current.push(val);
      this.valorInterno = current;
      this.emitChange();
    },
  },
};
</script>

<style scoped>
.parametro-valor-input {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.pvi-toggle {
  display: inline-flex;
  border: 1px solid #e4e7eb;
  border-radius: 6px;
  overflow: hidden;
  align-self: flex-start;
}

.pvi-toggle__btn {
  background: #fff;
  border: none;
  padding: 4px 10px;
  font-size: 11px;
  font-weight: 600;
  color: #667085;
  cursor: pointer;
  transition: all 0.15s;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.pvi-toggle__btn + .pvi-toggle__btn {
  border-left: 1px solid #e4e7eb;
}

.pvi-toggle__btn.active {
  background: #3a3e81;
  color: #fff;
}

.pvi-toggle__btn:hover:not(.active) {
  background: #f1f3f5;
}

.form-control-sm {
  font-size: 13px;
  border-radius: 6px;
}

/* ===== MULTI SELECT (mesmo visual do TabelaSelecaoTagFiltro) ===== */
.multi-select-container {
  border: 1px solid #ced4da;
  border-radius: 6px;
  background: #fff;
  overflow: hidden;
}

.multi-select-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  padding: 6px 8px;
  min-height: 34px;
  align-items: center;
}

.multi-select-tag {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px;
  background: #e3e3ee;
  color: #3a3e81;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
}

.multi-select-tag-remove {
  cursor: pointer;
  font-size: 14px;
  line-height: 1;
  opacity: 0.7;
}

.multi-select-tag-remove:hover {
  opacity: 1;
}

.multi-select-placeholder {
  color: #98a2b3;
  font-size: 13px;
}

.multi-select-options {
  border-top: 1px solid #e9ecef;
  max-height: 150px;
  overflow-y: auto;
  padding: 4px 0;
}

.multi-select-option {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 10px;
  font-size: 13px;
  color: #344054;
  cursor: pointer;
  margin: 0;
  transition: background 0.1s;
}

.multi-select-option:hover {
  background: #f9fafb;
}

.multi-select-option input[type="checkbox"] {
  accent-color: #3a3e81;
}
</style>
